import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { apiGetDeck, apiSearchWords } from 'core/api';
import { wait } from 'core/helpers/browser';
import usePromiseQuery from 'core/helpers/usePromiseQuery';
import { useMessageModal } from 'core/ui/MessageModal';

import { TWordSearchFilters } from '../../../shared/types/domain';
import useWordsApi from './useWordsApi';

type TRefState = {
  cursor?: string;
  filters: TWordSearchFilters;
  abort: null | (() => void);
};

type TState = {
  words: TWord[];
  cursor: string | undefined;
  filters: TWordSearchFilters;
};

export default function useWordList() {
  const { showError } = useMessageModal();

  const [deck, setDeck] = useState<TDeck | null>(null);
  const [wordsSt, setWordsSt] = useState<TState>({
    words: [],
    cursor: undefined,
    filters: { text: '', sort: 'newest' },
  });

  const deckUid = orDie(useParams<{ deckUid?: string }>().deckUid);

  const [doSearch, isFetching] = usePromiseQuery(apiSearchWords);

  const { current: wordsRef } = useRef<TRefState>({ abort: null } as TRefState);
  wordsRef.cursor = wordsSt.cursor;
  wordsRef.filters = wordsSt.filters;

  /**
   * Fetch from the scratch without pagination
   */
  const fetchInitial = useCallback(() => {
    wordsRef.abort?.();
    let isAborted = false;
    wordsRef.abort = () => { isAborted = true; }; // prettier-ignore

    // reset active state to show empty list and preloader
    setWordsSt(st => ({ ...st, words: [], cursor: undefined }));

    return doSearch(deckUid, undefined /* no cursor */, wordsRef.filters)
      .then(({ words, cursor }) => {
        if (isAborted) return;

        wordsRef.cursor = cursor;
        wordsRef.abort = null;
        setWordsSt(st => ({ ...st, cursor, words }));
      })
      .catch(showError);
  }, [deckUid]);

  useEffect(() => {
    Promise.all([apiGetDeck(deckUid), fetchInitial()])
      .then(([deck]) => {
        setDeck(deck);
      })
      .catch(showError);
  }, [deckUid]);

  const refFetching = useRef(isFetching);
  refFetching.current = isFetching;

  /**
   * Subsequent fetches with a cursor
   */
  const fetchMore = useCallback(() => {
    const isFetching = refFetching.current;
    const { cursor, filters } = wordsRef;

    /*
     * it's important to get these values from the ref, because state values
     * may become obsolete before fetchMore is updated (dependencies won't
     * help with it, because fetchMore may be called multiple times after state
     * was changed but before any render happened)
     */
    if (!cursor || isFetching) return;

    wordsRef.abort?.();
    let isAborted = false;
    wordsRef.abort = () => { isAborted = true; }; // prettier-ignore

    doSearch(deckUid, cursor, filters)
      .then(({ words, cursor }) => {
        if (isAborted) return;

        wordsRef.cursor = cursor;
        wordsRef.abort = null;
        setWordsSt(st => ({
          ...st,
          words: [...st.words, ...words],
          cursor,
        }));
      })
      .catch(showError);
  }, [deckUid]);

  const wordsApi = useWordsApi(
    useCallback((fn: (prev: TWord[]) => TWord[]): void => {
      setWordsSt(st => ({
        ...st,
        words: fn(st.words),
      }));
    }, [])
  );

  const setFilter = useCallback(
    <T extends keyof TWordSearchFilters>(
      key: T,
      value: TWordSearchFilters[T]
    ): void => {
      setWordsSt(st => ({
        ...st,
        filters: {
          ...st.filters,
          [key]: value,
        },
      }));
      if (key === 'sort') {
        // wait to upd. the ref-state.
        wait(0).then(fetchInitial).catch(showError);
      }
    },
    []
  );

  return {
    fetchInitial,
    fetchMore,
    words: wordsSt.words,
    filters: wordsSt.filters,
    setFilter,
    isFetching,
    deck,
    deckUid,
    ...wordsApi,
  };
}
